import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    Truck, CheckCircle2, Clock, MapPin,
    Plus, Map as MapIcon, ListOrdered, Wand2, Calendar as CalendarIcon,
    Trash2, ChevronUp, ChevronDown, Share2, Search, X
} from 'lucide-react';
import { toast } from 'sonner';
import DeliveryMap from '../components/DeliveryMap';

const STATUS_CONFIG = {
    pending: { label: 'À livrer', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
    delivered: { label: 'Livré', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
    issue: { label: 'Problème', color: 'bg-red-100 text-red-700 border-red-200', icon: Truck },
};

const NewDeliveryDialog = ({ open, onOpenChange, onSave, quotes }) => {
    const [formData, setFormData] = useState({ quote_id: 'none', client_name: '', address: '', delivery_date: null, items: [] });
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    useEffect(() => { if (open && !formData.delivery_date) setFormData(prev => ({ ...prev, delivery_date: new Date() })); }, [open]);
    const handleQuoteChange = (quoteId) => {
        if (quoteId === 'none') { setFormData({ ...formData, quote_id: 'none', client_name: '', address: '', items: [] }); return; }
        const quote = quotes.find(q => q.id === quoteId);
        if (quote) setFormData({ ...formData, quote_id: quoteId, client_name: quote.client_name || '', address: '', delivery_date: quote.start_date ? new Date(quote.start_date) : new Date(), items: quote.items.map(i => ({ name: i.product_name, quantity: i.quantity })) });
    };
    const handleSubmit = (e) => { e.preventDefault(); if (!formData.delivery_date) return toast.error("Date requise"); onSave(formData); };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Planifier une livraison</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2"><Label>Lier à un devis signé</Label><Select onValueChange={handleQuoteChange} value={formData.quote_id}><SelectTrigger><SelectValue placeholder="Sélectionner un devis" /></SelectTrigger><SelectContent><SelectItem value="none">Aucun</SelectItem>{quotes.map(q => <SelectItem key={q.id} value={q.id}>{q.reference} - {q.client_name}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-2"><Label>Client</Label><Input value={formData.client_name} onChange={e => setFormData({ ...formData, client_name: e.target.value })} required /></div>
                    <div className="space-y-2"><Label>Adresse de livraison</Label><Input value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} required /></div>
                    <div className="space-y-2"><Label>Date</Label><Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}><PopoverTrigger asChild><Button variant="outline" className={`w-full justify-start text-left font-normal ${!formData.delivery_date && "text-muted-foreground"}`}><CalendarIcon className="mr-2 h-4 w-4" />{formData.delivery_date ? format(formData.delivery_date, 'dd/MM/yyyy', { locale: fr }) : <span>Choisir une date</span>}</Button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={formData.delivery_date} onSelect={(date) => { setFormData({ ...formData, delivery_date: date }); setDatePopoverOpen(false); }} locale={fr} initialFocus /></PopoverContent></Popover></div>
                    <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button><Button type="submit">Planifier</Button></div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const DeliveriesPage = () => {
    const { token } = useAuthStore();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [localDeliveries, setLocalDeliveries] = useState([]);
    const [viewMode, setViewMode] = useState('list');
    const [depot, setDepot] = useState(null);
    const [isTourDialogOpen, setIsTourDialogOpen] = useState(false);
    const [driverName, setDriverName] = useState('');

    // FIX ID MAPPING ICI
    const { data: deliveries = [], isLoading } = useQuery({
        queryKey: ['deliveries'],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/deliveries`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            // On s'assure que chaque objet a une clé "id" propre pour le frontend
            return data.map(d => ({ ...d, id: d.id || d._id }));
        },
    });

    // RÉCUPÉRATION DU DÉPÔT (ADRESSE ENTREPRISE)
    useQuery({
        queryKey: ['company-settings'],
        queryFn: async () => {
            try {
                const response = await fetch(`${API_URL}/settings/company`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const res = await response.json();

                // LOG 1 : Vérifier ce que le backend envoie réellement
                console.log("Données reçues du backend :", res);

                // On vérifie les noms des champs (parfois c'est company_address au lieu de address)
                const address = res.address || res.company_address;
                const city = res.city || res.company_city;
                const cp = res.postal_code || res.zip_code;

                if (res && (address || city)) {
                    const full = `${address || ''} ${cp || ''} ${city || ''}`.trim();

                    if (!full) return res;

                    const geoResponse = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(full)}&limit=1`);
                    const geo = await geoResponse.json();


                    if (geo && geo.length > 0) {
                        setDepot({
                            latitude: parseFloat(geo[0].lat),
                            longitude: parseFloat(geo[0].lon),
                            address: full
                        });
                    } else {
                        // Si Nominatim échoue avec l'adresse complète, on tente juste avec la ville
                        console.warn("Adresse précise non trouvée, tentative avec la ville uniquement...");
                        const geoCityRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city)}&limit=1`);
                        const geoCity = await geoCityRes.json();

                        if (geoCity && geoCity.length > 0) {
                            setDepot({
                                latitude: parseFloat(geoCity[0].lat),
                                longitude: parseFloat(geoCity[0].lon),
                                address: city
                            });
                        }
                    }
                }
                return res;
            } catch (error) {
                console.error("Erreur complète du chargement dépôt :", error);
                return {};
            }
        }
    });

    useEffect(() => {
        if (deliveries.length > 0) {
            setLocalDeliveries([...deliveries].sort((a, b) => (a.route_order || 0) - (b.route_order || 0)));
        } else setLocalDeliveries([]);
    }, [deliveries]);

    const displayItems = useMemo(() => {
        if (!search) return localDeliveries;
        const t = search.toLowerCase();
        return localDeliveries.filter(d => d.client_name?.toLowerCase().includes(t) || d.address?.toLowerCase().includes(t));
    }, [localDeliveries, search]);

    const tourMutation = useMutation({
        mutationFn: async () => (await fetch(`${API_URL}/tours`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ driver_name: driverName, date: new Date().toISOString(), delivery_ids: localDeliveries.map(d => String(d.id)) }) })).json(),
        onSuccess: (data) => {
            setIsTourDialogOpen(false);
            if (data?.token) {
                navigator.clipboard.writeText(`${window.location.origin}/portal/tour/${data.token}`);
                toast.success("Tournée enregistrée !");
                navigate('/deliveries/history');
            }
        }
    });

    const updateOrderMutation = useMutation({
        mutationFn: async ({ id, order }) => {
            if (!id || id === "undefined") return;
            return fetch(`${API_URL}/deliveries/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ route_order: order })
            });
        }
    });

    const moveItem = (index, direction) => {
        const newItems = [...localDeliveries];
        const target = direction === 'up' ? index - 1 : index + 1;
        if (target < 0 || target >= newItems.length) return;
        [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
        setLocalDeliveries(newItems);

        // On utilise item.id (qui est maintenant garanti par le mapping du useQuery)
        updateOrderMutation.mutate({ id: newItems[index].id, order: index });
        updateOrderMutation.mutate({ id: newItems[target].id, order: target });
    };

    const optimizeRoute = () => {
        if (!depot) return toast.error("Adresse de l'entreprise (Dépôt) non configurée dans les paramètres.");
        const toOpt = localDeliveries.filter(d => d.latitude && d.longitude);
        const others = localDeliveries.filter(d => !d.latitude || !d.longitude);
        if (toOpt.length === 0) return toast.info("Aucune coordonnée GPS disponible pour l'optimisation.");

        let curr = { lat: depot.latitude, lng: depot.longitude };
        const opt = []; const rem = [...toOpt];
        const dist = (p1, p2) => Math.sqrt(Math.pow(p1.lat - p2.latitude, 2) + Math.pow(p1.lng - p2.longitude, 2));

        while (rem.length > 0) {
            let near = 0; let min = 999999;
            rem.forEach((item, i) => { const d = dist(curr, item); if (d < min) { min = d; near = i; } });
            opt.push(rem[near]);
            curr = { lat: rem[near].latitude, lng: rem[near].longitude };
            rem.splice(near, 1);
        }

        const final = [...opt, ...others].map((item, i) => ({ ...item, route_order: i }));
        setLocalDeliveries(final);
        final.forEach((item, i) => updateOrderMutation.mutate({ id: item.id, order: i }));
        toast.success("Tournée optimisée par rapport au dépôt !");
    };

    const statusMutation = useMutation({
        mutationFn: async ({ id, status }) => fetch(`${API_URL}/deliveries/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status }) }),
        onSuccess: () => queryClient.invalidateQueries(['deliveries'])
    });

    const deleteMutation = useMutation({
        mutationFn: async (id) => fetch(`${API_URL}/deliveries/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }),
        onSuccess: () => queryClient.invalidateQueries(['deliveries'])
    });

    const createMutation = useMutation({
        mutationFn: async (data) => {
            const safeDate = new Date(data.delivery_date); safeDate.setHours(12, 0, 0, 0);
            return (await fetch(`${API_URL}/deliveries`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ ...data, delivery_date: safeDate.toISOString(), route_order: localDeliveries.length }) })).json();
        },
        onSuccess: () => { queryClient.invalidateQueries(['deliveries']); setIsDialogOpen(false); toast.success('Livraison planifiée'); }
    });

    const { data: quotes = [] } = useQuery({
        queryKey: ['quotes', 'signed'],
        queryFn: async () => {
            const all = await (await fetch(`${API_URL}/quotes`, { headers: { Authorization: `Bearer ${token}` } })).json();
            return all.filter(q => ['signed', 'accepted'].includes(q.status)).map(q => ({ ...q, id: q.id || q._id }));
        }
    });

    return (
        <div className="space-y-6 h-[calc(100vh-100px)] flex flex-col">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 shrink-0">
                <div><h1 className="text-2xl font-heading font-bold">Livraisons</h1><p className="text-muted-foreground text-sm">Logistique & Tournées</p></div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate('/deliveries/history')}><ListOrdered className="w-4 h-4 mr-2" /> Historique</Button>
                    <Button variant="secondary" onClick={optimizeRoute}><Wand2 className="w-4 h-4 mr-2" /> Optimiser</Button>
                    <Button variant="outline" onClick={() => setIsTourDialogOpen(true)} disabled={localDeliveries.length === 0}><Share2 className="w-4 h-4 mr-2" /> Valider la tournée</Button>
                    <Button onClick={() => setIsDialogOpen(true)}><Plus className="w-4 h-4 mr-2" /> Planifier</Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 shrink-0">
                <Card className="bg-muted/30 p-3 flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Sélection</p><p className="text-2xl font-bold">{displayItems.length}</p></div><div className="bg-blue-100 p-2 rounded-full text-blue-600"><Truck className="w-5 h-5" /></div></Card>
                <div className="col-span-2 flex items-center gap-2">
                    <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Rechercher un client, une adresse..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 pr-10 bg-muted/20 border-none h-12" />{search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}</div>
                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-lg border h-12"><Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" onClick={() => setViewMode('list')} className="h-10 w-10"><ListOrdered className="w-4 h-4" /></Button><Button variant={viewMode === 'map' ? 'secondary' : 'ghost'} size="icon" onClick={() => setViewMode('map')} className="h-10 w-10"><MapIcon className="w-4 h-4" /></Button></div>
                </div>
            </div>

            <div className="flex-1 grid lg:grid-cols-3 gap-6 min-h-0">
                <div className={`lg:col-span-${viewMode === 'map' ? '1' : '3'} overflow-y-auto pr-2`}>
                    {isLoading ? <Skeleton className="h-40 w-full" /> : (
                        <div className="flex flex-col gap-3 pb-4">
                            {displayItems.map((delivery, idx) => (
                                <Card key={delivery.id} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4 flex gap-3 items-start">
                                        <div className="flex flex-col gap-1 shrink-0 pt-0.5"><Button variant="ghost" size="icon" className="h-7 w-7 rounded-full bg-muted/50" disabled={idx === 0} onClick={() => moveItem(idx, 'up')}><ChevronUp className="h-4 w-4" /></Button><div className="text-[10px] font-bold text-center text-primary">{idx + 1}</div><Button variant="ghost" size="icon" className="h-7 w-7 rounded-full bg-muted/50" disabled={idx === localDeliveries.length - 1} onClick={() => moveItem(idx, 'down')}><ChevronDown className="h-4 w-4" /></Button></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-start mb-1"><h4 className="font-bold truncate">{delivery.client_name}</h4><Badge className={`${STATUS_CONFIG[delivery.status]?.color || 'bg-gray-100'} text-[10px] px-1.5 py-0`}>{STATUS_CONFIG[delivery.status]?.label || 'Inconnu'}</Badge></div>
                                            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1 truncate"><MapPin className="w-3 h-3" /> {delivery.address}</p>
                                            <div className="flex justify-between items-center mt-3"><p className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(delivery.delivery_date)}</p><div className="flex gap-2">{delivery.status === 'pending' && <Button size="sm" variant="ghost" className="h-7 text-xs px-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" onClick={() => statusMutation.mutate({ id: delivery.id, status: 'delivered' })}>Valider</Button>}<Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => window.confirm("Supprimer ?") && deleteMutation.mutate(delivery.id)}><Trash2 className="w-3.5 h-3.5" /></Button></div></div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
                {(viewMode === 'map' || window.innerWidth > 1024) && <div className="hidden lg:block lg:col-span-2 h-full rounded-xl overflow-hidden border shadow-sm relative"><DeliveryMap deliveries={displayItems} depot={depot} /></div>}
            </div>

            <NewDeliveryDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} onSave={(data) => createMutation.mutate(data)} quotes={quotes} />
            <Dialog open={isTourDialogOpen} onOpenChange={setIsTourDialogOpen}><DialogContent><DialogHeader><DialogTitle>Enregistrer la tournée</DialogTitle></DialogHeader><div className="space-y-4 pt-4"><div className="space-y-2"><Label>Chauffeur / Véhicule</Label><Input value={driverName} onChange={e => setDriverName(e.target.value)} placeholder="Ex: Marc - Master FR-123-AA" /></div><p className="text-sm text-muted-foreground">Fige l'ordre ({localDeliveries.length} arrêts) et génère un lien de suivi.</p><Button className="w-full" onClick={() => tourMutation.mutate()} disabled={!driverName || tourMutation.isPending}>{tourMutation.isPending ? "Génération..." : "Générer la tournée"}</Button></div></DialogContent></Dialog>
        </div>
    );
};

export default DeliveriesPage;